import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { AclGroupsPageStore, KEY_JOIN } from './acl-groups-page.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { GroupObject } from '@it2go/types';
import { ActionEnum } from '../../../../../../../../types/src/authorization/enum/action.enum';
import { fb, isAllowed } from '../../../../shared/shared.global-service';
import { FormGroup, Validators } from '@angular/forms';
import { GroupCreateInput } from '@libs/types/src/user/input-type/group/group.create-input';
import { GroupUpdateInput } from '@libs/types/src/user/input-type/group/group.update-input';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import {
    MultiSelectInputComponent,
} from '../../../../../components/kit/input/multi-select-input/multi-select-input.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { DeleteButtonComponent } from '../../../../../components/kit/button/delete-button.component';
import { CardModule } from '@openng/optimus-ui/card';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { LinkButtonComponent } from '../../../../../components/kit/button/link-button.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-acl-groups-page',
    templateUrl: 'acl-groups-page.component.html',
    styleUrl: 'acl-groups-page.component.scss',
    providers: [AclGroupsPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonsComponent,
        AsyncPipe,
        ButtonComponent,
        CardModule,
        DeleteButtonComponent,
        DialogModule,
        LinkButtonComponent,
        MultiSelectInputComponent,
        PrimeTemplate,
        TextInputComponent,
        TranslatePipe,
    ],
})
export class AclGroupsPageComponent extends SubscriptionComponent {

    protected readonly store = inject(AclGroupsPageStore);

    public modalVisible = false;

    protected selected: GroupObject | null = null;
    protected readonly prefix = 'acl.group.';
    protected readonly KEY_JOIN = KEY_JOIN;

    protected form: FormGroup = fb.group({
        name: fb.control<string>('', Validators.required),
        children: fb.control<number[]>([]),
        rules: fb.group({}),
    });

    constructor() {
        super();
        this.store.onRouteChange('allAvailableGroups', { organizationId: 'organizationId' });
        this.store.doOnActions(async () => {
            this.store.api.allAvailableGroups({ organizationId: Number(this.store.routeParam.organizationId.value) });
        }, 'createGroup', 'updateGroup', 'deleteGroup');

        this.store.registerSuccessMessage('createGroup', 'common.created');
        this.store.registerSuccessMessage('updateGroup', 'common.updated');
        this.store.registerSuccessMessage('deleteGroup', 'common.deleted');

        this.subs.push(
            // TODO Losos !important
            // this.store.allResources$.subscribe((it) => {
            //     this.form.controls.rules = fb.group(it.reduce((acc, rule: any) => {
            //         Object.values(ActionEnum).forEach((action) => {
            //             const key = `${rule.resource}${this.KEY_JOIN}${action}`;
            //
            //             acc[key] = fb.control<boolean>(!!rule[action]);
            //             if (!rule[action]) {
            //                 (acc[key] as FormControl).disable();
            //             }
            //         });
            //         return acc;
            //     }, {} as Record<string, any>));
            // }),
        );
    }

    public showModal(visible: boolean = true): void {
        this.modalVisible = visible;
    }

    protected select(item: GroupObject | null): void {
        this.form.patchValue({
            name: item?.name || '',
            children: item?.children || [],
        });
        this.selected = item;

        const rules: any = {};
        item?.rules?.forEach((it) => {
            rules[`${it.resource}${this.KEY_JOIN}${it.action}`] = true;
        });

        (this.form.controls.rules as FormGroup).reset();
        this.form.controls.rules.patchValue(rules);

        this.showModal();
    }

    protected selectAll(resource: string): void {
        const keys = Object.values(ActionEnum).map((it) => `${resource}${this.KEY_JOIN}${it}`);
        const toSet = !!keys.find((key) => !this.form.controls.rules.value[key] && !(this.form.controls.rules as any).controls[key].disabled);
        keys.forEach((key) => {
            if (!(this.form.controls.rules as any).controls[key].disabled) {
                this.form.controls.rules.patchValue({ [key]: toSet });
            }
        });
    }

    protected async delete(event: any): Promise<void> {
        this.store.api.deleteGroup({ id: this.selected!.id });
        this.showModal(false);

        // this.confirm.confirm(
        //     'common.delete',
        //     null,
        //     () => {
        //         this.store.api.deleteGroup({ id: this.selected!.id });
        //         this.showModal(false);
        //     },
        //     null,
        //     'pi-trash',
        //     event.target,
        // );
    }

    protected async submit(): Promise<void> {
        if (this.form.isInvalid()) return;

        const _data = {
            ...this.form.value,
            rules: Object.keys(this.form.value.rules).map((key) => {
                const [resource, action] = key.split(this.KEY_JOIN);
                return this.form.controls.rules.value[key] ? { resource, action } : null;
            }).filter((it) => it),
        };

        if (this.selected) {
            const data = _data as GroupUpdateInput;
            data.id = this.selected.id;
            this.store.api.updateGroup(data);
        } else {
            const data = _data as GroupCreateInput;
            data.organizationId = Number(this.store.routeParam.organizationId.value);
            this.store.api.createGroup(data);
        }

        this.showModal(false);
    }

    protected readonly isAllowed = isAllowed;
    protected readonly actions = Object.values(ActionEnum);
}
